import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchClassFormOptions } from '@/features/class/manage/api';
import {
  CLASS_NAME_MAX_LENGTH,
  CLASS_TYPE_BACKEND_IDS,
  CLASS_TYPE_OPTIONS,
} from '@/features/class/manage/constants';
import { hasFormErrors, validateClassForm } from '@/features/class/manage/classFormValidation';
import { useClassManageStore } from '@/features/class/manage/classManageStore';
import { useCreateBoardMutation } from '@/features/class/useCreateBoard';
import { useDeleteBoardMutation } from '@/features/class/useDeleteBoard';
import { useUpdateBoardMutation } from '@/features/class/useUpdateBoard';
import { useArchiveBoardMutation } from '@/features/class/useArchiveBoard';
import { useUnarchiveBoardMutation } from '@/features/class/useUnarchiveBoard';
import { useBoardDetailQuery } from '@/features/class/useBoardDetail';
import { useAuthStore } from '@/store/authStore';
import {
  MOCK_ACADEMIC_YEARS,
  MOCK_GRADE_LEVELS,
  MOCK_SUBJECTS,
} from '@/features/class/manage/mockFormOptions';
import type {
  ClassFormErrors,
  ClassFormValues,
  ManagedClass,
  SelectOption,
} from '@/features/class/manage/types';
import Modal from '@/shared/ui/Modal';
import ModalHeader from '@/shared/ui/ModalHeader';
import FormField from '@/shared/ui/FormField';
import Input from '@/shared/ui/Input';
import Select from '@/shared/ui/Select';
import SearchableSelect from '@/shared/ui/SearchableSelect';
import Checkbox from '@/shared/ui/Checkbox';
import Button from '@/shared/ui/Button';
import { textStyles } from '@/shared/tokens/typography';
import { cn } from '@/shared/lib/cn';

const CLASS_TYPE_LABEL_KEYS: Record<ClassFormValues['classType'] | '', string> = {
  '': '',
  homeroom: 'typeHomeroom',
  subject: 'typeSubject',
  communication: 'typeCommunication',
};

const EMPTY_FORM: ClassFormValues = {
  classType: '',
  name: '',
  academicYearId: '',
  subjectId: '',
  gradeLevelId: '',
  credits: '',
  isPrivate: false,
  notifyFollowers: false,
};

function valuesFromClass(item: ManagedClass): ClassFormValues {
  return {
    classType: item.classType,
    name: item.name,
    academicYearId: item.academicYearId,
    subjectId: item.subjectId ?? '',
    gradeLevelId: item.gradeLevelId,
    credits: item.credits !== undefined ? String(item.credits) : '',
    isPrivate: item.isPrivate,
    notifyFollowers: false,
  };
}

function pickStringValue(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

function pickString(source: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const value = pickStringValue(source[key]);
    if (value) return value;
  }
  return undefined;
}

function pickBoolean(
  source: Record<string, unknown>,
  keys: readonly string[],
): boolean | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number' && Number.isFinite(value)) return value !== 0;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes'].includes(normalized)) return true;
      if (['false', '0', 'no'].includes(normalized)) return false;
    }
  }
  return undefined;
}

function classTypeFromApi(value: unknown): ClassFormValues['classType'] | undefined {
  const raw =
    value && typeof value === 'object' && !Array.isArray(value)
      ? pickString(value as Record<string, unknown>, ['id', 'classType', 'type', 'value', 'code'])
      : pickStringValue(value);
  const normalized = raw?.toLowerCase();
  if (!normalized) return undefined;
  if (normalized === '1' || normalized.includes('homeroom') || normalized.includes('form')) {
    return 'homeroom';
  }
  if (normalized === '2' || normalized.includes('subject')) {
    return 'subject';
  }
  if (
    normalized === '3' ||
    normalized.includes('communication') ||
    normalized.includes('channel')
  ) {
    return 'communication';
  }
  return undefined;
}

/** Extract a `{ id, label }` option from a backend taxonomy ref (`{ id, name }`). */
function taxonomyRefToOption(ref: unknown, fallbackId: unknown): SelectOption | null {
  if (ref && typeof ref === 'object') {
    const r = ref as Record<string, unknown>;
    const id = pickString(r, ['id', 'subjectId', 'masterSubjectId', 'gradeId', 'value', 'code']);
    const label = pickString(r, ['name', 'subjectName', 'gradeName', 'label', 'title', 'value']);
    if (id && label) {
      return { id, label };
    }
  }
  if (fallbackId != null && String(fallbackId).trim()) {
    return { id: String(fallbackId), label: String(fallbackId) };
  }
  return null;
}

function valuesFromBoardDetail(
  detail: Record<string, unknown>,
  fallback: ClassFormValues,
): ClassFormValues {
  const subject = taxonomyRefToOption(
    detail.subject ?? detail.masterSubject,
    pickString(detail, ['subjectId', 'masterSubjectId']),
  );
  const grade = taxonomyRefToOption(
    detail.grade ?? detail.masterGrade ?? detail.gradeLevel,
    pickString(detail, ['gradeId', 'masterGradeId', 'gradeLevelId']),
  );
  const classType = classTypeFromApi(detail.classType ?? detail.classTypeId ?? detail.type);
  const isPrivate = pickBoolean(detail, ['isPrivate', 'private']);
  const notifyFollowers = pickBoolean(detail, ['notifyToFollowers', 'notifyFollowers']);

  return {
    classType: classType ?? fallback.classType,
    name: pickString(detail, ['name', 'boardName', 'className']) ?? fallback.name,
    academicYearId:
      pickString(detail, ['schoolYear', 'academicYearId', 'academicYear', 'year']) ??
      fallback.academicYearId,
    subjectId: subject?.id ?? fallback.subjectId,
    gradeLevelId: grade?.id ?? fallback.gradeLevelId,
    credits: pickString(detail, ['credit', 'credits']) ?? fallback.credits,
    isPrivate: isPrivate ?? fallback.isPrivate,
    notifyFollowers: notifyFollowers ?? fallback.notifyFollowers,
  };
}

/**
 * Merge a real backend option into a mock option list so the field can display
 * the class's actual value. If an option with the same id exists, its label is
 * overridden (the real name — e.g. "Pre-K" — wins over the mock placeholder);
 * otherwise the real option is prepended.
 */
function withInjectedOption(
  options: readonly SelectOption[],
  injected: SelectOption | null,
): readonly SelectOption[] {
  if (!injected) return options;
  const index = options.findIndex((option) => option.id === injected.id);
  if (index === -1) return [injected, ...options];
  const next = [...options];
  next[index] = injected;
  return next;
}

interface ClassFormModalProps {
  mode: 'create' | 'edit';
  editingClass: ManagedClass | null;
  boardId?: string;
  onClose: () => void;
}

export default function ClassFormModal({
  mode,
  editingClass,
  boardId,
  onClose,
}: ClassFormModalProps) {
  const { t } = useTranslation('class');
  const { t: tCommon } = useTranslation('common');
  const createClass = useClassManageStore((state) => state.createClass);
  const updateClass = useClassManageStore((state) => state.updateClass);
  const archiveClass = useClassManageStore((state) => state.archiveClass);
  const restoreClass = useClassManageStore((state) => state.restoreClass);
  const deleteClass = useClassManageStore((state) => state.deleteClass);

  // Real backend call for "Create class" — runs in addition to the
  // optimistic Zustand insert so the UX is unchanged. POST /midl/api/v3/board
  const createBoardMutation = useCreateBoardMutation();
  // Real backend call for "Delete class" — runs in addition to the optimistic
  // Zustand removal. DELETE /midl/api/v3/board?id=<boardId>&userId=<userId>
  const deleteBoardMutation = useDeleteBoardMutation();
  // Real backend call for "Save changes" — runs in addition to the optimistic
  // Zustand update. PUT /midl/api/v3/board/<boardId>
  const updateBoardMutation = useUpdateBoardMutation();
  // Real backend call for "Archive class" — runs in addition to the optimistic
  // Zustand archive. POST /midl/api/v3/archive-board/<boardId>
  const archiveBoardMutation = useArchiveBoardMutation();
  // Real backend call for "Move to profile" (unarchive) — runs in addition to
  // the optimistic Zustand restore. POST /midl/api/v3/un-archive-board/<boardId>
  const unarchiveBoardMutation = useUnarchiveBoardMutation();
  const authUserId = useAuthStore((state) => state.user?.id ?? null);

  const [values, setValues] = useState<ClassFormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<ClassFormErrors>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const boundBoardDetailIdRef = useRef<string | null>(null);

  const classFormOptionsQuery = useQuery({
    queryKey: ['class', 'manage', 'form-options'],
    queryFn: fetchClassFormOptions,
    staleTime: 5 * 60_000,
  });

  const isEdit = mode === 'edit';
  const isArchivedEdit = Boolean(isEdit && editingClass?.archived);
  const realBoardId =
    isEdit && boardId
      ? boardId
      : isEdit && editingClass && /^\d+$/.test(editingClass.id)
        ? editingClass.id
        : undefined;

  useEffect(() => {
    boundBoardDetailIdRef.current = null;
    if (isEdit && editingClass) {
      setValues(valuesFromClass(editingClass));
    } else {
      setValues(EMPTY_FORM);
    }
    setErrors({});
    setConfirmDelete(false);
  }, [editingClass, isEdit, mode]);

  // For real API boards (numeric id) fetch the full detail so the Subject and
  // Year/Grade fields can show the class's actual backend values. Seeded/mock
  // classes (non-numeric ids like "class-math101") already carry their form
  // data, so we skip the network call for them.
  const boardDetailQuery = useBoardDetailQuery(realBoardId);
  const boardDetail = boardDetailQuery.data ?? null;
  const isEditDetailLoading = Boolean(realBoardId && boardDetailQuery.isLoading);

  // Real `{ id, label }` for Subject / Grade taken from the backend detail
  // (`subject`/`grade` objects, falling back to the raw `subjectId`/`gradeId`).
  const detailSubject = useMemo(() => {
    if (!boardDetail) return null;
    return taxonomyRefToOption(
      boardDetail.subject ?? boardDetail.masterSubject,
      pickString(boardDetail, ['subjectId', 'masterSubjectId']),
    );
  }, [boardDetail]);
  const detailGrade = useMemo(() => {
    if (!boardDetail) return null;
    return taxonomyRefToOption(
      boardDetail.grade ?? boardDetail.masterGrade ?? boardDetail.gradeLevel,
      pickString(boardDetail, ['gradeId', 'masterGradeId', 'gradeLevelId']),
    );
  }, [boardDetail]);
  const detailAcademicYear = useMemo(() => {
    if (!boardDetail) return null;
    const schoolYear = pickString(boardDetail, [
      'schoolYear',
      'academicYearId',
      'academicYear',
      'year',
    ]);
    return schoolYear ? { id: schoolYear, label: schoolYear } : null;
  }, [boardDetail]);

  // Dropdown option lists with the real backend value merged in so the field
  // renders the actual class Subject / Year-Grade even when it isn't part of
  // the master-data option set.
  const academicYearOptions = useMemo(
    () =>
      withInjectedOption(
        classFormOptionsQuery.data?.academicYears ?? MOCK_ACADEMIC_YEARS,
        detailAcademicYear,
      ),
    [classFormOptionsQuery.data?.academicYears, detailAcademicYear],
  );
  const subjectOptions = useMemo(
    () => withInjectedOption(classFormOptionsQuery.data?.subjects ?? MOCK_SUBJECTS, detailSubject),
    [classFormOptionsQuery.data?.subjects, detailSubject],
  );
  const gradeOptions = useMemo(
    () =>
      withInjectedOption(classFormOptionsQuery.data?.gradeLevels ?? MOCK_GRADE_LEVELS, detailGrade),
    [classFormOptionsQuery.data?.gradeLevels, detailGrade],
  );

  // When editing a real API board, bind the form from GET /board/{id}. Do this
  // once per board id so later background refetches don't overwrite user edits.
  useEffect(() => {
    if (!isEdit || !editingClass || !realBoardId || !boardDetail) return;
    if (boundBoardDetailIdRef.current === realBoardId) return;
    setValues(valuesFromBoardDetail(boardDetail, valuesFromClass(editingClass)));
    setErrors({});
    boundBoardDetailIdRef.current = realBoardId;
  }, [boardDetail, editingClass, isEdit, realBoardId]);

  const title = isEdit ? t('formEditTitle') : t('formCreateTitle');
  const submitLabel = isEdit ? tCommon('saveChanges') : t('formCreateSubmit');
  const nameCharactersRemaining = CLASS_NAME_MAX_LENGTH - values.name.length;

  const patchValues = useCallback((patch: Partial<ClassFormValues>) => {
    setValues((previous) => ({ ...previous, ...patch }));
    setErrors({});
  }, []);

  const handleSubmit = useCallback(() => {
    if (isEditDetailLoading) {
      return;
    }

    const nextErrors = validateClassForm(values, isEdit);
    if (hasFormErrors(nextErrors)) {
      setErrors(nextErrors);
      return;
    }

    const backendClassType = values.classType
      ? CLASS_TYPE_BACKEND_IDS[values.classType]
      : CLASS_TYPE_BACKEND_IDS.subject;

    if (isEdit && editingClass) {
      // Optimistic local update (existing behaviour — also closes the modal).
      updateClass(editingClass.id, values);

      // Persist to the backend for real boards (numeric board id):
      //   PUT /midl/api/v3/board/<boardId>
      //   body: { name, categoryId, userId, gradeId, subjectId, description, credit, currency, amount, notifyToFollowers, connectingAllowed, schoolYear, classType, isPrivate, deviceType }
      // Seeded / mock classes have non-numeric ids (e.g. "class-math101")
      // and stay local-only.
      if (authUserId && realBoardId) {
        updateBoardMutation.mutate({
          boardId: realBoardId,
          name: values.name.trim(),
          isPrivate: values.isPrivate,
          userId: authUserId,
          deviceType: 'web',
          categoryId: null,
          subjectId: values.subjectId || null,
          gradeId: values.gradeLevelId,
          description: '',
          credit: values.credits.trim(),
          currency: '',
          amount: 0,
          notifyToFollowers: values.notifyFollowers,
          connectingAllowed: null,
          schoolYear: values.academicYearId,
          classType: backendClassType,
        });
      } else if (import.meta.env.DEV) {
        console.warn('[ClassFormModal] skipping PUT /board (local update only)', editingClass.id);
      }
      return;
    }

    // Optimistic local insert (existing behaviour — UI unchanged).
    createClass(values);

    // Persist to the backend in parallel:
    //   POST /midl/api/v3/board
    //   body: { name, isPrivate, userId, deviceType: "web" }
    // The mutation is fire-and-forget here — failures are logged in dev
    // and the local row stays so the user isn't blocked. If we ever want
    // to roll back on failure we can add an `onError` that removes the
    // class by name from the Zustand store.
    if (authUserId) {
      createBoardMutation.mutate({
        name: values.name.trim(),
        isPrivate: values.isPrivate,
        userId: authUserId,
        deviceType: 'web',
        categoryId: null,
        subjectId: values.subjectId || undefined,
        gradeId: values.gradeLevelId,
        description: '',
        credit: values.credits.trim(),
        currency: '',
        amount: 0,
        notifyToFollowers: values.notifyFollowers,
        connectingAllowed: null,
        schoolYear: values.academicYearId,
        classType: backendClassType,
      });
    } else if (import.meta.env.DEV) {
      console.warn(
        '[ClassFormModal] no auth user id — skipping POST /board (local create still applied)',
      );
    }
  }, [
    authUserId,
    createBoardMutation,
    createClass,
    editingClass,
    isEdit,
    isEditDetailLoading,
    realBoardId,
    updateBoardMutation,
    updateClass,
    values,
  ]);

  const handleArchive = useCallback(() => {
    if (!editingClass) {
      return;
    }

    // Fire the real backend archive for real boards (numeric board id). Seeded
    // / mock classes have non-numeric ids (e.g. "class-math101") and stay
    // local-only. The mutation removes the board from `rawUser` on success so
    // the sidebar/profile drop it from the active list without a reload.
    if (realBoardId) {
      archiveBoardMutation.mutate({ boardId: realBoardId });
    } else if (import.meta.env.DEV) {
      console.warn(
        '[ClassFormModal] skipping PUT /archive-board (local archive only)',
        editingClass.id,
      );
    }

    // Optimistic local archive (existing behaviour — also closes the modal).
    archiveClass(editingClass.id);
  }, [archiveBoardMutation, archiveClass, editingClass, realBoardId]);

  const handleRestore = useCallback(() => {
    if (!editingClass) {
      return;
    }

    // Fire the real backend unarchive ("Move to profile") for real boards
    // (numeric board id). Seeded / mock classes have non-numeric ids (e.g.
    // "class-math101") and stay local-only. On success the joined-boards and
    // archived-classes queries are invalidated so the class moves from the
    // Archived page back to the active profile/sidebar list.
    if (realBoardId) {
      unarchiveBoardMutation.mutate({ boardId: realBoardId });
    } else if (import.meta.env.DEV) {
      console.warn(
        '[ClassFormModal] skipping POST /un-archive-board (local restore only)',
        editingClass.id,
      );
    }

    // Optimistic local restore (existing behaviour).
    restoreClass(editingClass.id);
    onClose();
  }, [editingClass, onClose, realBoardId, restoreClass, unarchiveBoardMutation]);

  const handleDelete = useCallback(() => {
    if (!editingClass) {
      return;
    }
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    // Fire the real backend DELETE for real boards (numeric board id). Seeded
    // / mock classes have non-numeric ids (e.g. "class-math101") and stay
    // local-only. The mutation removes the board from `rawUser` on success so
    // the sidebar/profile update without a reload.
    if (authUserId && realBoardId) {
      deleteBoardMutation.mutate({ boardId: realBoardId, userId: authUserId });
    } else if (import.meta.env.DEV) {
      console.warn('[ClassFormModal] skipping DELETE /board (local delete only)', editingClass.id);
    }

    // Optimistic local removal (existing behaviour — also closes the modal).
    deleteClass(editingClass.id);
  }, [authUserId, confirmDelete, deleteBoardMutation, deleteClass, editingClass, realBoardId]);

  return (
    <Modal
      onClose={onClose}
      zIndexClassName="z-[100]"
      className="flex max-h-[min(92vh,720px)] w-full max-w-lg flex-col"
      ariaLabel={title}
    >
      <ModalHeader title={title} onClose={onClose} />

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <FormField
            label={t('formClassTypeLabel')}
            htmlFor="class-type"
            required
            error={errors.classType}
          >
            <Select
              id="class-type"
              value={values.classType}
              disabled={isEditDetailLoading}
              onChange={(event) =>
                patchValues({ classType: event.target.value as ClassFormValues['classType'] })
              }
              hasError={Boolean(errors.classType)}
            >
              <option value="">{t('formSelectClassType')}</option>
              {CLASS_TYPE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {t(CLASS_TYPE_LABEL_KEYS[option.id])}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField
            label={t('formClassNameLabel')}
            htmlFor="class-name"
            required
            error={errors.name}
            hint={t('formCharactersRemaining', { count: nameCharactersRemaining })}
          >
            <Input
              id="class-name"
              value={values.name}
              disabled={isEditDetailLoading}
              onChange={(event) => patchValues({ name: event.target.value })}
              placeholder={t('formClassNamePlaceholder')}
              maxLength={CLASS_NAME_MAX_LENGTH}
              hasError={Boolean(errors.name)}
            />
          </FormField>

          <FormField
            label={t('formAcademicYearLabel')}
            htmlFor="class-academic-year"
            required
            error={errors.academicYearId}
          >
            <Select
              id="class-academic-year"
              value={values.academicYearId}
              disabled={isEditDetailLoading}
              onChange={(event) => patchValues({ academicYearId: event.target.value })}
              hasError={Boolean(errors.academicYearId)}
            >
              <option value="">{t('formSelectAcademicYear')}</option>
              {academicYearOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField
            label={t('formSubjectLabel')}
            htmlFor="class-subject"
            hint={t('formSubjectHint')}
          >
            <SearchableSelect
              id="class-subject"
              value={values.subjectId}
              disabled={isEditDetailLoading}
              onChange={(subjectId) => patchValues({ subjectId })}
              options={subjectOptions}
              placeholder={t('formSelectSubjectOptional')}
              searchPlaceholder={t('formSearchSubjects')}
              emptyOptionLabel={t('formNoSubject')}
            />
          </FormField>

          <FormField
            label={t('formGradeLabel')}
            htmlFor="class-grade"
            required
            error={errors.gradeLevelId}
          >
            <Select
              id="class-grade"
              value={values.gradeLevelId}
              disabled={isEditDetailLoading}
              onChange={(event) => patchValues({ gradeLevelId: event.target.value })}
              hasError={Boolean(errors.gradeLevelId)}
            >
              <option value="">{t('formSelectGrade')}</option>
              {gradeOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label={t('formCreditsLabel')} htmlFor="class-credits" error={errors.credits}>
            <Input
              id="class-credits"
              type="number"
              min={0}
              step={0.5}
              value={values.credits}
              disabled={isEditDetailLoading}
              onChange={(event) => patchValues({ credits: event.target.value })}
              placeholder={t('formCreditsPlaceholder')}
              hasError={Boolean(errors.credits)}
            />
          </FormField>

          <div className="space-y-3 rounded-xl border border-gray-300 bg-gray-50/60 px-4 py-3">
            <Checkbox
              id="class-private"
              checked={values.isPrivate}
              disabled={isEditDetailLoading}
              onChange={(event) => patchValues({ isPrivate: event.target.checked })}
              label={t('formPrivateLabel')}
            />
            <p className={cn(textStyles.caption, '-mt-1 pl-7')}>{t('formPrivateDescription')}</p>

            {!isEdit ? (
              <>
                <Checkbox
                  id="class-notify-followers"
                  checked={values.notifyFollowers}
                  onChange={(event) => patchValues({ notifyFollowers: event.target.checked })}
                  label={t('formNotifyFollowersLabel')}
                />
                <p className={cn(textStyles.caption, '-mt-1 pl-7')}>
                  {t('formNotifyFollowersDescription')}
                </p>
              </>
            ) : null}
          </div>

          {isEdit ? (
            <div className="space-y-3 border-t border-gray-300 pt-4">
              {isArchivedEdit ? (
                <>
                  <p className={textStyles.label}>{t('formArchivedLabel')}</p>
                  <p className={textStyles.caption}>{t('formArchivedDescription')}</p>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" onClick={handleRestore}>
                      {t('formMoveToProfile')}
                    </Button>
                    <Button type="button" variant="danger" onClick={handleDelete}>
                      {confirmDelete ? tCommon('confirmDelete') : t('formDeleteClass')}
                    </Button>
                    {confirmDelete ? (
                      <Button type="button" variant="ghost" onClick={() => setConfirmDelete(false)}>
                        {tCommon('cancel')}
                      </Button>
                    ) : null}
                  </div>
                </>
              ) : (
                <>
                  <p className={textStyles.label}>{t('formDangerZone')}</p>
                  <p className={textStyles.caption}>{t('formDangerZoneDescription')}</p>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="secondary" onClick={handleArchive}>
                      {t('formArchiveClass')}
                    </Button>
                    <Button type="button" variant="danger" onClick={handleDelete}>
                      {confirmDelete ? tCommon('confirmDelete') : t('formDeleteClass')}
                    </Button>
                    {confirmDelete ? (
                      <Button type="button" variant="ghost" onClick={() => setConfirmDelete(false)}>
                        {tCommon('cancel')}
                      </Button>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          ) : null}
        </form>
      </div>

      <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-gray-300 px-4 py-3 sm:px-6">
        <Button type="button" variant="ghost" onClick={onClose}>
          {tCommon('cancel')}
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={isEditDetailLoading}>
          {submitLabel}
        </Button>
      </div>
    </Modal>
  );
}
